export const ARTWORK_LOAD_TIMEOUT_MS = 15000
export const GUIDED_BUILD_TIMEOUT_MS = 120000
const RETRY_DELAYS_MS = [0, 250, 1000, 2500]

function aborted() {
  return new DOMException('Artwork loading cancelled', 'AbortError')
}

function pause(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(aborted())
    const cancel = () => { clearTimeout(timer); reject(aborted()) }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', cancel)
      resolve()
    }, ms)
    signal?.addEventListener('abort', cancel, { once: true })
  })
}

// Bound URL lookup and image decoding together. Abort supported transports and
// settle even if a loader ignores abort; late results never reach the caller.
function loadAttempt(resolveUrl, loadImage, attempt, signal, timeoutMs, releaseUrl) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(aborted())
    const controller = new AbortController()
    let settled = false
    let resolvedUrl
    const finish = (callback, value) => {
      if (settled) {
        if (value?.url) releaseUrl?.(value.url)
        else if (callback === reject && resolvedUrl) releaseUrl?.(resolvedUrl)
        return
      }
      settled = true
      clearTimeout(timer)
      signal?.removeEventListener('abort', cancel)
      if (callback === reject && resolvedUrl) releaseUrl?.(resolvedUrl)
      callback(value)
    }
    const cancel = () => {
      finish(reject, aborted())
      controller.abort()
    }
    const timer = setTimeout(() => {
      finish(reject, new Error('Artwork loading timed out'))
      controller.abort()
    }, timeoutMs)
    signal?.addEventListener('abort', cancel, { once: true })
    Promise.resolve().then(async () => {
      const url = await resolveUrl(attempt, controller.signal)
      resolvedUrl = url
      if (controller.signal.aborted) throw aborted()
      const image = await loadImage(url, controller.signal)
      return { image, url }
    }).then(result => finish(resolve, result), error => finish(reject, error))
  })
}

export async function loadArtworkImage({ resolveUrl, loadImage, signal, onAttempt, releaseUrl, timeoutMs = ARTWORK_LOAD_TIMEOUT_MS }) {
  let lastError
  for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt += 1) {
    if (signal?.aborted) throw aborted()
    if (attempt > 0) await pause(RETRY_DELAYS_MS[attempt], signal)
    onAttempt?.(attempt + 1)
    try {
      return await loadAttempt(resolveUrl, loadImage, attempt, signal, timeoutMs, releaseUrl)
    } catch (error) {
      if (signal?.aborted) throw aborted()
      lastError = error
    }
  }
  throw lastError
}
